import type { ReactNode } from 'react'
import { Building2, Users, Shield } from 'lucide-react'

const features = [
  { Icon: Building2, label: 'Gestión de empresas y subsidiarias' },
  { Icon: Users,     label: 'Administración de usuarios y roles' },
  { Icon: Shield,    label: 'Seguridad empresarial con Auth0' },
]

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen">

      {/* ══════════════════════════════════════════════
          LEFT PANEL — brand / marketing (desktop only)
      ══════════════════════════════════════════════ */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #0a1628 0%, #0d2050 40%, #1a56db 100%)' }}
      >
        {/* Decorative glow blobs */}
        <div
          className="absolute top-[-150px] right-[-150px] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(6,102,235,0.35) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-100px] left-[-100px] w-[350px] h-[350px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(6,102,235,0.2) 0%, transparent 70%)' }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-[10px] flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            <Building2 size={20} color="#ffffff" />
          </div>
          <span className="font-display text-xl font-bold tracking-tight text-white">
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Kash</span>io
          </span>
        </div>

        {/* Tagline + feature list + decorative card */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-3">
            <h2
              className="font-display text-[1.875rem] font-bold leading-tight text-white"
              style={{ letterSpacing: '-0.5px' }}
            >
              Gestiona tu empresa<br />con total control
            </h2>
            <p
              className="text-base leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.6)', maxWidth: '340px' }}
            >
              Plataforma empresarial centralizada para administrar empresas,
              usuarios y roles de manera eficiente.
            </p>
          </div>

          <ul className="space-y-4">
            {features.map(({ Icon, label }) => (
              <li key={label} className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.1)' }}
                >
                  <Icon size={16} color="rgba(255,255,255,0.85)" />
                </div>
                <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  {label}
                </span>
              </li>
            ))}
          </ul>

          {/* Faux-terminal decorative card */}
          <div
            className="rounded-2xl p-5"
            style={{
              background: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ffbd2e' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
            </div>
            <div className="space-y-2">
              {[75, 90, 55].map((w, i) => (
                <div
                  key={i}
                  className="h-2 rounded-full"
                  style={{ width: `${w}%`, background: 'rgba(255,255,255,0.15)' }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="relative z-10 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
          © Kashio {new Date().getFullYear()} · Plataforma empresarial segura
        </p>
      </div>

      {/* ══════════════════════════════════════════
          RIGHT PANEL — form area
      ══════════════════════════════════════════ */}
      <div className="flex flex-1 items-center justify-center px-6 py-10 bg-bg-secondary">
        <div className="w-full max-w-[420px] space-y-5">

          {/* Mobile-only logo */}
          <div className="flex flex-col items-center gap-3 lg:hidden">
            <div
              className="w-12 h-12 rounded-[14px] flex items-center justify-center"
              style={{ background: '#0666eb', boxShadow: '0 4px 14px rgba(6,102,235,0.4)' }}
            >
              <Building2 size={22} color="#ffffff" />
            </div>
            <span className="font-display text-2xl font-bold text-text-primary">
              <span style={{ color: '#274074' }}>Kash</span>
              <span style={{ color: '#0666eb' }}>io</span>
            </span>
          </div>

          {/* Form card */}
          <div
            className="w-full rounded-2xl border border-border-secondary bg-bg-primary px-9 py-10"
            style={{ boxShadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)' }}
          >
            {children}
          </div>

        </div>
      </div>

    </div>
  )
}
